import { Box, Stack, Typography } from '@mui/material';
import PaiAutoComplete, { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiRoundTextField from 'features/ui/base-components/pai-round-text-field';
import usePipelineStore from 'stores/pipeline-store';
import { useUserData } from 'hooks/use-user-data';
import { Funnel, FunnelStatuses, FunnelTypes, FunnelContact } from 'features/co-pilot/co-pilot.types';
import { getAllFunnels } from 'API/linkedin-funnel';
import { getAnsweredContacts, getOverviewAnsweredContacts, getPipelineKpis } from 'API/funnel-contact-management';
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PipelineBoard from 'features/pipeline/pipeline-board';
import { buildColumns, PipelineSegment } from 'features/pipeline/pipeline-utils';
import { useLocation } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import PaiDatePicker from 'features/ui/base-components/pai-date-picker';
import PipelineKpiRow from 'features/pipeline/pipeline-kpi-row';

const OVERALL_ID = '__overall__';

const PipelinePage: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t, i18n } = useTranslation();
  const { token } = useUserData();
  const { selectedFunnelId, funnelType, recruitingMode, searchTerm, contacts, setSelectedFunnel, setRecruitingMode, setSearchTerm, initialiseBoard } =
    usePipelineStore((state) => ({
      selectedFunnelId: state.selectedFunnelId,
      funnelType: state.funnelType,
      recruitingMode: state.recruitingMode,
      searchTerm: state.searchTerm,
      contacts: state.contacts,
      setSelectedFunnel: state.setSelectedFunnel,
      setRecruitingMode: state.setRecruitingMode,
      setSearchTerm: state.setSearchTerm,
      initialiseBoard: state.initialiseBoard,
    }));

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const contextParam = searchParams.get('context');

  const isRecruitingContext =
    contextParam === 'recruiting' || location.pathname.includes('recruiting') || location.pathname.includes('hr-booster');

  interface CampaignOption extends OptionType {
    funnelType: FunnelTypes;
  }

  const [campaignOptions, setCampaignOptions] = useState<CampaignOption[]>([
    { label: t('overall'), value: OVERALL_ID, funnelType: FunnelTypes.SOCIAL_SELLING_CO_PILOT as any },
  ]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  const setActiveTab = useAppBarNavigationStore((state) => state.setActiveTab);

  // Date filter state
  const defaultFrom = new Date('2025-01-01T00:00:00Z');
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo, setDateTo] = useState<Date>(new Date());

  const [reloadKey, setReloadKey] = useState(0);

  // KPI state
  const [kpis, setKpis] = useState<any>({});
  const [loadingKpis, setLoadingKpis] = useState(false);

  const handleStatusUpdated = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  // Set navbar active tab on mount
  useEffect(() => {
    setActiveTab(NavigationStates.PIPELINE);
  }, [setActiveTab]);

  // Fetch campaigns on mount
  useEffect(() => {
    const fetchFunnels = async () => {
      if (!token) return;
      setLoadingCampaigns(true);
      const funnelTypeFilter = isRecruitingContext ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT : FunnelTypes.SOCIAL_SELLING_CO_PILOT;
      const res = await getAllFunnels(funnelTypeFilter, undefined, undefined, undefined, undefined, undefined, undefined, true);
      if (!res.error) {
        const funnels: Funnel[] = res.campaigns || [];
        const filtered = funnels.filter((f) => f.status !== FunnelStatuses.ARCHIVED && f.status !== FunnelStatuses.CREATED);
        const mapped: CampaignOption[] = filtered.map((f) => ({ label: f.name, value: f._id, funnelType: f.type as FunnelTypes }));
        setCampaignOptions(mapped);
      } else {
        setCampaignOptions([]);
      }

      // if no campaign selected yet, default to overall
      if (!selectedFunnelId) {
        setSelectedFunnel(OVERALL_ID, null);
      }

      setLoadingCampaigns(false);
    };
    fetchFunnels();
  }, [token]);

  // Helper: filter contacts by search term
  const filterBySearch = (list: FunnelContact[]): FunnelContact[] => {
    if (!searchTerm?.trim()) return list;
    const term = searchTerm.trim().toLowerCase();
    return list.filter((c) => {
      const full = `${c.firstName ?? ''} ${c.lastName ?? ''}`.toLowerCase();
      return full.includes(term);
    });
  };

  useEffect(() => {
    const fetchContacts = async () => {
      if (!token) return;

      const segment = computeSegment();

      // Overall selection ➜ use overview endpoint
      if (!selectedFunnelId || selectedFunnelId === OVERALL_ID) {
        const typeParam = isRecruitingContext ? 'recruiting' : 'socialSelling';
        const dateFromIso = dateFrom ? dateFrom.toISOString().split('T')[0] : undefined;
        const dateToIso = dateTo ? dateTo.toISOString().split('T')[0] : undefined;
        const res = await getOverviewAnsweredContacts(typeParam as any, dateFromIso, dateToIso);
        if (!res.error) {
          const contactsAll: FunnelContact[] = [];
          const funnels: any[] = res.response?.funnels || [];
          funnels.forEach((f) => {
            const campaignName = f.name;
            const campaignId = f.id;
            (f.answeredContacts || []).forEach((c: any) => {
              (c as any).originFunnelName = campaignName;
              (c as any).originFunnelId = campaignId;
              contactsAll.push(c);
            });
          });
          const filtered = filterBySearch(contactsAll);
          const columns = buildColumns(filtered, segment);
          initialiseBoard(contactsAll, columns);
        }
        return;
      }

      // Single funnel selected
      const res = await getAnsweredContacts(selectedFunnelId);
      if (!res.error) {
        const contacts: FunnelContact[] = res.response || [];
        const filtered = filterBySearch(contacts);
        const columns = buildColumns(filtered, segment);
        initialiseBoard(contacts, columns);
      }
    };
    fetchContacts();
  }, [selectedFunnelId, token, campaignOptions.length, dateFrom, dateTo, reloadKey]);

  // Rebuild columns instantly on recruitingMode toggle using existing contacts (avoids extra API round-trip)
  useEffect(() => {
    if (!contacts || contacts.length === 0) return;

    const segment = computeSegment();
    const updatedColumns = buildColumns(filterBySearch(contacts), segment);
    initialiseBoard(contacts, updatedColumns);
  }, [recruitingMode, funnelType, isRecruitingContext, searchTerm]);

  // fetch KPIs whenever filters change
  useEffect(() => {
    const fetchKpis = async () => {
      if (!token) return;
      setLoadingKpis(true);
      const params: any = {};
      if (selectedFunnelId && selectedFunnelId !== OVERALL_ID) params.funnelId = selectedFunnelId;
      else if (isRecruitingContext) params.type = funnelType === FunnelTypes.ACTIVE_SOURCING_CO_PILOT ? undefined : 'sales';
      const dateFromIso = dateFrom ? dateFrom.toISOString().split('T')[0] : undefined;
      const dateToIso = dateTo ? dateTo.toISOString().split('T')[0] : undefined;
      if (dateFromIso) params.dateFrom = dateFromIso;
      if (dateToIso) params.dateTo = dateToIso;
      const res = await getPipelineKpis(params);
      if (!res.error) setKpis(res.response);
      setLoadingKpis(false);
    };
    fetchKpis();
  }, [selectedFunnelId, dateFrom, dateTo, token, reloadKey]);

  const handleCampaignChange = (event: any, newValue: OptionType | OptionType[] | null) => {
    if (Array.isArray(newValue)) return; // We only allow single selection
    const id = newValue?.value || OVERALL_ID; // null clears selection back to overall
    const found = campaignOptions.find((o) => o.value === id);
    setSelectedFunnel(id, found?.funnelType ?? null);
  };

  const handleRecruitingModeChange = (mode: 'internal' | 'external') => () => {
    setRecruitingMode(mode);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
  };

  const handleDateFromChange = (newDate: any) => {
    if (newDate instanceof Date && !isNaN(newDate.getTime())) setDateFrom(newDate);
  };

  const handleDateToChange = (newDate: any) => {
    if (newDate instanceof Date && !isNaN(newDate.getTime())) setDateTo(newDate);
  };

  // helper to decide current pipeline segment
  const computeSegment = (): PipelineSegment => {
    if (isRecruitingContext || funnelType === FunnelTypes.ACTIVE_SOURCING_CO_PILOT) {
      return recruitingMode === 'external' ? 'externalRecruiting' : 'internalRecruiting';
    }
    return 'sales';
  };

  // determine current segment for rendering
  const currentSegment: PipelineSegment = computeSegment();

  const showRecruitingToggle = isRecruitingContext || funnelType === FunnelTypes.ACTIVE_SOURCING_CO_PILOT;

  const formatDate = (date: Date): string =>
    new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);

  const selectedCampaign = campaignOptions.find((o) => o.value === selectedFunnelId) || null;
  const campaignLabel = selectedFunnelId && selectedFunnelId !== OVERALL_ID ? selectedCampaign?.label ?? '' : t('allCampaigns');

  const subtitleParts = [campaignLabel, `${formatDate(dateFrom)} – ${formatDate(dateTo)}`, t('pipelineContactsCount', { count: contacts?.length ?? 0 })];

  // enable clearing dropdown
  const autoCompleteProps = { disableClearable: false } as any;

  return (
    <Box className={classes.page}>
      <Stack className={classes.card}>
        {/* Header: title + filters */}
        <Box className={classes.header}>
          <Stack gap={'6px'}>
            <Typography className={classes.title}>{t('pipeline')}</Typography>
            <Typography className={classes.subtitle}>{subtitleParts.filter(Boolean).join(' · ')}</Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={'12px'} flexWrap="wrap" justifyContent="flex-end">
            <PaiAutoComplete
              options={campaignOptions}
              placeholder={t('selectCampaign') as string}
              selected={selectedCampaign}
              disabled={loadingCampaigns}
              onChange={handleCampaignChange}
              style={{ width: 220 }}
              {...autoCompleteProps}
            />
            <PaiDatePicker value={dateFrom} onChange={handleDateFromChange} style={{ width: 160 }} />
            <PaiDatePicker value={dateTo} onChange={handleDateToChange} style={{ width: 160 }} />
            <PaiRoundTextField
              variant="search"
              placeholder={t('searchContacts') as string}
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              style={{ width: 220 }}
            />
            {showRecruitingToggle && (
              <Stack direction="row" className={classes.segmented}>
                <Typography
                  className={cx(classes.segment, recruitingMode !== 'external' && classes.segmentActive)}
                  onClick={handleRecruitingModeChange('internal')}
                >
                  {t('pipelineInternal')}
                </Typography>
                <Typography
                  className={cx(classes.segment, classes.segmentDivided, recruitingMode === 'external' && classes.segmentActive)}
                  onClick={handleRecruitingModeChange('external')}
                >
                  {t('pipelineExternal')}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* KPIs – visible instead of hidden in an accordion */}
        <Box className={classes.kpiSection}>
          <PipelineKpiRow kpis={kpis} loading={loadingKpis} segment={currentSegment} />
        </Box>

        {/* Kanban board */}
        <Box className={classes.boardSection}>
          <PipelineBoard segment={currentSegment} funnelId={selectedFunnelId} allContacts={contacts} onStatusUpdated={handleStatusUpdated} />
        </Box>
      </Stack>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  page: {
    padding: theme.spacing(2),
  },
  card: {
    background: theme.palette.backgrounds.main_new,
    borderRadius: '6px',
    boxShadow: '0 4px 12px 0 rgba(0,0,0,0.12)',
  },
  header: {
    padding: '28px 32px 20px 32px',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '24px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  segmented: {
    border: `1px solid ${theme.palette.greys.lighter}`,
    background: '#FFF',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  segment: {
    height: 40,
    display: 'flex',
    alignItems: 'center',
    padding: '0 14px',
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.dark,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  segmentDivided: {
    borderLeft: `1px solid ${theme.palette.greys.lighter}`,
  },
  segmentActive: {
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  kpiSection: {
    padding: '0 32px 20px 32px',
  },
  boardSection: {
    padding: '0 32px 32px 32px',
  },
}));

export default PipelinePage;
